import {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import Client from '../adapters/Client';
import { isClientReady } from '../adapters/utils';
import useAuthHeaders from '../hooks/useAuthHeaders';
import { HeaderData, IClient } from '../adapters/types';

type ClientContextValue = IClient | null;

const ClientContext = createContext<ClientContextValue>(null);

const ClientProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [authorizedClient, setAuthorizedClient] = useState<IClient>(Client);
  const [headerData, setHeaderData] = useState<HeaderData>(Client.headers);

  const { isReady, headers } = useAuthHeaders();

  useEffect(() => {
    if (isReady && headers?.authorization) {
      setHeaderData((prev) => ({
        ...prev,
        ...headers,
      }));
    }
  }, [isReady, headers]);

  useEffect(() => {
    setAuthorizedClient((prev) => ({
      ...prev,
      headers: headerData,
    }));
  }, [headerData]);

  const memoizedClient = useMemo(
    () => ({ ...authorizedClient }),
    [authorizedClient],
  );

  return (
    <ClientContext.Provider
      value={isClientReady(memoizedClient) ? memoizedClient : null}
    >
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = (): ClientContextValue => useContext(ClientContext);

export default ClientProvider;
